import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, G, LinearGradient, Path, Rect, Stop } from 'react-native-svg';

import { useSvgId } from '../../../utils/hooks/useSvgId';
import { ACHIEVEMENTS_FONT } from '../Achievements/achievementsTheme';
import { Pressable } from '../common/Touchable';

/**
 * The Edit Profile "Locked" username field's two extras (Figma "Field / Edit Profile", State=Locked):
 * the lock glyph right-aligned in the input, and the "Button / Claim" CTA under the helper.
 * Both are drawn in the mobile design units the caller passes, so the desktop twin can reuse
 * them at its 1.25x numbers.
 */

/** "Status Icon / Lock": the 13.46-wide art centred in a square, at 55% opacity. */
export const LockGlyph = ({ size }: { size: number }) => (
	<Svg width={size} height={size} viewBox='0 0 17.5 17.5'>
		<G opacity={0.55} transform='translate(2.019 0)'>
			<Path
				d='M3.04517 6.71876V4.84376C3.04517 3.8492 3.45039 2.89537 4.17169 2.19211C4.89298 1.48885 5.87126 1.09376 6.89133 1.09376C7.91139 1.09376 8.88967 1.48885 9.61097 2.19211C10.3323 2.89537 10.7375 3.8492 10.7375 4.84376V6.71876'
				fill='none'
				stroke='#A88E86'
				strokeWidth={1.34615}
			/>
			<Path
				d='M10.7375 4.77424H3.04519C1.9831 4.77424 1.12211 5.61371 1.12211 6.64924V12.2742C1.12211 13.3098 1.9831 14.1492 3.04519 14.1492H10.7375C11.7996 14.1492 12.6606 13.3098 12.6606 12.2742V6.64924C12.6606 5.61371 11.7996 4.77424 10.7375 4.77424Z'
				fill='#A88E86'
			/>
			<Path d='M6.73077 7.74303V11.493' stroke='#330E07' strokeWidth={1.73077} />
		</G>
	</Svg>
);

type ButtonProps = {
	onPress: () => void;
	height: number;
	radius: number;
	rim: number;
	/** Blur of the green "Button/Glow/Green" bloom. */
	glow: number;
	fontSize: number;
	letterSpacing: number;
};

/** "Button / Claim" in its Default state: green fill, mint rim, green bloom, gold label. */
export const BuyUsernameChangerButton = ({
	onPress,
	height,
	radius,
	rim,
	glow,
	fontSize,
	letterSpacing,
}: ButtonProps) => {
	const fillId = useSvgId('buyUsernameChanger');
	const { t } = useTranslation();
	return (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={t('T_SITE_A11Y_BUY_USERNAME_CHANGER', {
				defaultValue: 'Buy username changer',
			})}
			onPress={onPress}
			style={[
				styles.button,
				{
					height,
					borderRadius: radius,
					boxShadow: `0px 0px ${glow}px 0px rgba(64, 255, 51, 0.45)`,
				},
			]}
		>
			<View
				pointerEvents='none'
				style={[StyleSheet.absoluteFill, { borderRadius: radius, overflow: 'hidden' }]}
			>
				<Svg width='100%' height='100%'>
					<Defs>
						<LinearGradient id={fillId} x1='0' y1='0' x2='0' y2='1'>
							<Stop offset='0' stopColor='#66D93D' />
							<Stop offset='0.55' stopColor='#248F1C' />
							<Stop offset='1' stopColor='#0D540F' />
						</LinearGradient>
					</Defs>
					<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${fillId})`} />
				</Svg>
			</View>
			<View
				pointerEvents='none'
				style={[StyleSheet.absoluteFill, styles.rim, { borderRadius: radius, borderWidth: rim }]}
			/>
			<Text
				numberOfLines={1}
				style={[styles.label, { fontSize, lineHeight: fontSize * 1.3, letterSpacing }]}
			>
				{t('T_SITE_A11Y_BUY_USERNAME_CHANGER', { defaultValue: 'Buy username changer' })}
			</Text>
		</Pressable>
	);
};

const styles = StyleSheet.create({
	button: { alignItems: 'center', justifyContent: 'center' },
	rim: { borderColor: '#B8FF8C' },
	label: {
		color: '#FFF5B9',
		fontFamily: ACHIEVEMENTS_FONT,
		fontWeight: '900',
		textTransform: 'uppercase',
		includeFontPadding: false,
	},
});
